// Single source of truth for all UI copy. Add new keys here whenever a
// component needs a new translatable string. Testimonial bodies in
// Reviews.jsx stay in their original language and are deliberately not
// included in this file.

export const TRANSLATIONS = {
  en: {
    lang: { switchLabel: "Language" },
    nav: {
      home: "HOME",
      services: "SERVICES",
      training: "WORKSHOPS",
      references: "REFERENCES",
      contact: "CONTACT",
    },
    hero: {
      headline: {
        it: "IT",
        change: "CHANGE",
        with: "WITH",
        impact: "IMPACT!",
      },
      paragraph:
        "I help IT companies boost efficiency and manage change with real impact — no silver bullets, just solutions that improve.",
      domainsLabel: "Domains of support:",
      hashtags: [
        "#Mentoring for managers",
        "#Agile",
        "#Project Management",
        "#OKRs",
        "#Trainings",
        "#Workshops",
        "#Consultations",
      ],
      cta: "I WANT THE CHANGE",
    },
    intro: {
      eyebrow: "— Why collaborate together? —",
      headlineMain: "SUPPORTING PEOPLE, TEAMS AND ORGANIZATIONS",
      headlineAccent: "CHANGE!",
      paragraph:
        "Consulting, mentoring, and practical training for IT managers and teams. I combine 15+ years of experience with a personal, non-dogmatic approach. No silver bullets — only solutions that work in your reality.",
      steps: [
        {
          num: "01",
          title: "REVIEW",
          desc: "Assess goals, processes, and teams; surface bottlenecks, risks, and opportunities clearly.",
        },
        {
          num: "02",
          title: "DESIGN",
          desc: "Shape a pragmatic roadmap with concrete next steps, owners, and measurable outcomes.",
        },
        {
          num: "03",
          title: "IMPROVE",
          desc: "Execute changes hands-on with teams — mentoring leaders, unblocking work, raising the bar.",
        },
        {
          num: "04",
          title: "MAINTAIN",
          desc: "Embed habits, lightweight rituals, and feedback loops so improvements actually stick.",
        },
      ],
    },
    impact: {
      headlineLines: ["TRAINING,", "MENTORING,", "AND SUPPORTING", "FOR IMPACTFUL"],
      headlineAccent: "CHANGE!",
      paragraph:
        "I provide consulting, mentoring, trainings and workshops, auditing processes, and coaching leaders — with business impact as the key evidence of improvement.",
      cta: "I want the change",
      stats: [
        { value: "50+", label: "Workshops delivered" },
        { value: "15", label: "Years of helping teams" },
        { value: "150+", label: "People supported worldwide" },
        { value: "7500h", label: "Time spent mentoring & training" },
      ],
    },
    services: {
      eyebrow: "— What I offer —",
      title: "SERVICES",
      paragraph:
        "Each engagement is shaped around your context, environment, and business goals — so the scope and deliverables fit your reality and the solution stays practical.",
      filters: [
        "All Services",
        "Mentoring For Managers",
        "Agile Management Workshops",
        "Soft Skills Trainings",
        "OKR Certifications",
        "IT Consultations",
      ],
      focusDomainsLabel: "Focus domains",
      moreDetails: "Get more information",
      items: [
        {
          num: "01",
          title: "Mentoring For Managers",
          heading: "Management skills growth accelerated",
          body: [
            "Professional coaching for engineering managers, team leads, and team members accelerates real-world management skills through structured questions and finding the best path jointly, grounded in the problems you face right now.",
            "You strengthen skills like prioritization, delegation, feedback, and decision-making so your teams deliver more predictably with less chaos and reduced burnout risk.",
            "Each session ends with tangible next steps, with optional ad-hoc support between calls when something cannot wait.",
          ],
        },
        {
          num: "02",
          title: "Agile Management Workshops",
          heading: "Agile that fits your team — not the other way around",
          body: [
            "Practical workshops on Scrum, Kanban, OKRs, and flow metrics — tuned to your context. We focus on outcomes, not ceremonies.",
            "Hands-on exercises with real backlogs and team data so the changes stick after the workshop ends.",
          ],
        },
        {
          num: "03",
          title: "Soft Skills Trainings",
          heading: "Conversations and decisions that actually move people",
          body: [
            "Practical soft-skills training that improves how leaders run people, conversations, and pressure situations — without losing delivery focus.",
            "Sessions are built around real cases from your environment (performance dips, missed commitments, stakeholder conflict, low ownership, overloaded seniors), so participants practice the conversations and decisions they actually need at work.",
          ],
          domains: [
            { name: "Motivation", desc: "Understand what drives performance (and what kills it), then build practical habits and environment changes that make motivation sustainable." },
            { name: "Leadership", desc: "Lead through clarity, decisions, and accountability — especially under pressure and ambiguity." },
            { name: "Giving & Receiving Feedback", desc: "Deliver feedback that lands and drives change; receive feedback without defensiveness so learning speed goes up." },
            { name: "Coaching", desc: "Use coaching conversations and open-ended questions to grow ownership and problem-solving in your people." },
            { name: "Persuasion", desc: "Influence without formal authority by framing, aligning interests, and making trade-offs explicit." },
            { name: "Assertiveness", desc: "Set boundaries, say no, and communicate expectations clearly — without aggression or avoidance." },
            { name: "Conflict Resolution", desc: "Address tension early, de-escalate, and turn disagreements into clear agreements and next steps." },
            { name: "Facilitation & Moderation", desc: "Run meetings that produce decisions and commitment; handle dominant voices and keep groups focused." },
            { name: "Goal Setting", desc: "Set goals that drive outcomes; make progress measurable and connect goals to execution and priorities." },
            { name: "Time Management", desc: "Protect focus, manage overload, and build routines that reduce reactive work." },
            { name: "Burnout", desc: "Recognize early signals, fix systemic causes, and apply leader behaviors that keep performance stable without sacrificing people." },
          ],
          footer:
            "Typical format is 0.5–1 day (on-site or remote), highly interactive with role-plays, guided scripts, and concrete frameworks you can reuse immediately. Domains can be packaged as one training (multiple modules in a 1- or 2-day format) or as separate focused sessions — each with exercises, role-plays, and take-home templates.",
        },
        {
          num: "04",
          title: "OKR Certifications",
          heading: "Align Strategy and Execution with industry-standard OKRs",
          body: [
            "I am an accredited OKR Mentors Trainer, supporting organizations in implementing OKRs in a practical, sustainable and business-oriented way. I help leadership teams, managers and teams connect strategy with execution, improve prioritisation, create alignment and build a healthier rhythm for reviewing progress and making decisions.",
            "I provide OKR certification training, as well as consulting services for organisations that are introducing, restarting or improving their OKR practice. This includes leadership alignment, OKR design, facilitation of OKR cycles, review of Objectives and Key Results, check-in setup, retrospectives and hands-on support during implementation.",
          ],
        },
        {
          num: "05",
          title: "IT Consultations",
          heading: "Senior advisory on the issue blocking you now",
          body: [
            "On-demand senior consulting for delivery problems, team restructuring, hiring strategy, technical leadership, and organizational design.",
            "Short engagements with crisp recommendations and follow-up support.",
          ],
        },
      ],
    },
    mostRequested: {
      eyebrow: "— Most Requested Training —",
      headlineLines: ["CHANGE", "MANAGEMENT"],
      headlineAccent: "WORKSHOPS",
      paragraph:
        "As the first quarter of the 21st century remains turbulent, we can expect even more change ahead. Companies, teams, and individuals must adapt quickly to stay relevant to new trends and technological advances. Practical change-management tools, practices, and real examples help organizations cope with fast shifts and uncertainty.",
      cta: "Get more information",
    },
    references: {
      eyebrow: "— Trusted by —",
      titleMain: "CLIENT",
      titleAccent: "REFERENCES",
    },
    reviews: {
      eyebrow: "— Client Reviews —",
      titleMain: "Review &",
      titleAccent: "Recommendations",
    },
    cta: {
      headline1: "LET'S START",
      headline2: "GET ON BOARD,",
      headline3a: "SHALL",
      headline3b: "WE?",
      paragraph:
        "Pick a 30-minute intro call, ring me directly, or drop a message — I'll come back with a concrete next step within one working day.",
      phone: "+420 602 461 799",
      schedule: "Schedule a call",
      leave: "Leave a message",
    },
    footer: {
      tagline:
        "Consulting, mentoring, and practical training for IT managers and teams. No silver bullets — only solutions that work in your reality.",
      navigate: "Navigate",
      home: "Home",
      services: "Services",
      references: "References",
      reviews: "Reviews",
      getInTouch: "Get in touch",
      rights: "All rights reserved.",
      crafted: "Crafted for impactful change",
    },
  },

  cs: {
    lang: { switchLabel: "Jazyk" },
    nav: {
      home: "DOMŮ",
      services: "SLUŽBY",
      training: "ŠKOLENÍ",
      references: "REFERENCE",
      contact: "KONTAKT",
    },
    hero: {
      headline: {
        it: "IT",
        change: "ZMĚNA",
        with: "S",
        impact: "DOPADEM!",
      },
      paragraph:
        "Pomáhám IT firmám zvyšovat efektivitu a řídit změny s reálným dopadem — žádné zázračné recepty, jen řešení, která opravdu zlepšují.",
      domainsLabel: "Oblasti podpory:",
      hashtags: [
        "#Mentoring pro manažery",
        "#Agile",
        "#Projektové řízení",
        "#OKRs",
        "#Školení",
        "#Workshopy",
        "#Konzultace",
      ],
      cta: "CHCI ZMĚNU",
    },
    intro: {
      eyebrow: "— Proč spolupracovat? —",
      headlineMain: "POMÁHÁM LIDEM, TÝMŮM A ORGANIZACÍM",
      headlineAccent: "MĚNIT SE!",
      paragraph:
        "Konzultace, mentoring a praktická školení pro IT manažery a týmy. Spojuji 15+ let zkušeností s osobním, nedogmatickým přístupem. Žádné zázračné recepty — jen řešení, která fungují ve vaší realitě.",
      steps: [
        {
          num: "01",
          title: "ANALÝZA",
          desc: "Zhodnocení cílů, procesů a týmů; jasné odhalení úzkých míst, rizik a příležitostí.",
        },
        {
          num: "02",
          title: "NÁVRH",
          desc: "Pragmatická cesta s konkrétními dalšími kroky, vlastníky a měřitelnými výsledky.",
        },
        {
          num: "03",
          title: "ZLEPŠENÍ",
          desc: "Realizace změn přímo s týmy — mentoring lídrů, odblokování práce, zvyšování laťky.",
        },
        {
          num: "04",
          title: "UDRŽENÍ",
          desc: "Zakotvení návyků, lehkých rituálů a zpětnovazebních smyček, aby zlepšení vydržela.",
        },
      ],
    },
    impact: {
      headlineLines: ["ŠKOLENÍ,", "MENTORING", "A PODPORA", "PRO ZMĚNU S"],
      headlineAccent: "DOPADEM!",
      paragraph:
        "Poskytuji konzultace, mentoring, školení a workshopy, audity procesů a koučování lídrů — s obchodním dopadem jako klíčovým důkazem zlepšení.",
      cta: "Chci změnu",
      stats: [
        { value: "50+", label: "Odvedených workshopů" },
        { value: "15", label: "Let pomáhání týmům" },
        { value: "150+", label: "Podpořených lidí celosvětově" },
        { value: "7500h", label: "Hodin mentoringu a školení" },
      ],
    },
    services: {
      eyebrow: "— Co nabízím —",
      title: "SLUŽBY",
      paragraph:
        "Každá spolupráce se přizpůsobí vašemu kontextu, prostředí a obchodním cílům — rozsah i výstupy odpovídají vaší realitě a řešení zůstává praktické.",
      filters: [
        "Všechny služby",
        "Mentoring pro manažery",
        "Agile workshopy",
        "Soft Skills školení",
        "OKR Certifikace",
        "IT Konzultace",
      ],
      focusDomainsLabel: "Klíčové oblasti",
      moreDetails: "Získat více informací",
      items: [
        {
          num: "01",
          title: "Mentoring pro manažery",
          heading: "Zrychlený rozvoj manažerských dovedností",
          body: [
            "Profesionální koučování pro engineering manažery, team leady i členy týmů zrychluje rozvoj reálných manažerských dovedností pomocí strukturovaných otázek a společného hledání nejlepší cesty — vždy na základě problémů, které řešíte právě teď.",
            "Posílíte dovednosti jako prioritizace, delegování, zpětná vazba a rozhodování, aby vaše týmy doručovaly předvídatelněji, s menším chaosem a nižším rizikem vyhoření.",
            "Každé sezení končí konkrétními dalšími kroky, s možností ad-hoc podpory mezi hovory, když něco nemůže počkat.",
          ],
        },
        {
          num: "02",
          title: "Agile workshopy",
          heading: "Agile, který sedne vašemu týmu — ne naopak",
          body: [
            "Praktické workshopy o Scrumu, Kanbanu, OKRs a metrikách toku — vyladěné na váš kontext. Soustředíme se na výsledky, ne na ceremonie.",
            "Hands-on cvičení s reálnými backlogy a daty týmu, aby změny vydržely i po skončení workshopu.",
          ],
        },
        {
          num: "03",
          title: "Soft Skills školení",
          heading: "Rozhovory a rozhodnutí, které lidi opravdu posunou",
          body: [
            "Praktické soft-skills školení, které zlepšuje, jak lídři vedou lidi, rozhovory a tlakové situace — bez ztráty doručovacího zaměření.",
            "Sezení stavíme na reálných případech z vašeho prostředí (propady výkonu, nedodržené závazky, konflikty se stakeholdery, slabá zodpovědnost, přetížení seniorů), aby si účastníci nacvičili rozhovory a rozhodnutí, která skutečně potřebují v práci.",
          ],
          domains: [
            { name: "Motivace", desc: "Pochopení toho, co žene výkon (a co ho ničí), a budování praktických návyků a změn prostředí, které dělají motivaci udržitelnou." },
            { name: "Leadership", desc: "Vedení skrze jasnost, rozhodnutí a zodpovědnost — zejména pod tlakem a v nejistotě." },
            { name: "Dávání a přijímání zpětné vazby", desc: "Dávat zpětnou vazbu, která sedne a vede ke změně; přijímat zpětnou vazbu bez defenzivy, aby rychlost učení rostla." },
            { name: "Koučování", desc: "Využívat koučovací rozhovory a otevřené otázky k růstu zodpovědnosti a řešení problémů u vašich lidí." },
            { name: "Přesvědčování", desc: "Ovlivňovat bez formální autority — rámováním, sladěním zájmů a otevřenou diskusí o kompromisech." },
            { name: "Asertivita", desc: "Stanovovat hranice, říkat ne a jasně komunikovat očekávání — bez agrese i bez vyhýbání." },
            { name: "Řešení konfliktů", desc: "Adresovat napětí včas, deeskalovat a měnit neshody v jasné dohody a další kroky." },
            { name: "Facilitace a moderace", desc: "Vést porady, ze kterých vznikají rozhodnutí a závazky; zvládat dominantní hlasy a držet skupinu zaměřenou." },
            { name: "Stanovení cílů", desc: "Stanovovat cíle, které ženou výsledky; činit pokrok měřitelným a propojovat cíle s realizací a prioritami." },
            { name: "Time management", desc: "Chránit pozornost, zvládat přetížení a budovat rutiny, které snižují reaktivní práci." },
            { name: "Vyhoření (Burnout)", desc: "Rozpoznat rané signály, opravit systémové příčiny a uplatňovat chování lídra, které drží výkon stabilní bez obětování lidí." },
          ],
          footer:
            "Typický formát je 0,5–1 den (na místě nebo online), velmi interaktivní — s role-play scénáři, vodítky pro rozhovory a konkrétními rámci, které můžete okamžitě použít. Oblasti lze sbalit do jednoho školení (více modulů v 1- nebo 2denním formátu) nebo jako samostatná zaměřená sezení — každé s cvičeními, role-playi a šablonami na cestu domů.",
        },
        {
          num: "04",
          title: "OKR Certifikace",
          heading: "Sladění strategie a realizace pomocí standardních OKR",
          body: [
            "Jsem akreditovaný trenér OKR Mentors a podporuji organizace v zavádění OKR praktickým, udržitelným a byznysově orientovaným způsobem. Pomáhám vedení, manažerům a týmům propojit strategii s realizací, zlepšit prioritizaci, vytvořit alignment a vybudovat zdravější rytmus pro vyhodnocování pokroku a rozhodování.",
            "Poskytuji jak certifikační školení OKR, tak konzultační služby pro organizace, které OKR zavádějí, restartují nebo zlepšují svou OKR praxi. To zahrnuje alignment vedení, návrh OKR, facilitaci OKR cyklů, revizi Objectives a Key Results, nastavení check-inů, retrospektivy a praktickou podporu během implementace.",
          ],
        },
        {
          num: "05",
          title: "IT Konzultace",
          heading: "Senior poradenství k problému, který vás právě blokuje",
          body: [
            "On-demand senior konzultace pro problémy s dodávkami, restrukturalizaci týmů, strategii náboru, technické vedení a organizační design.",
            "Krátké spolupráce s ostrými doporučeními a navazující podporou.",
          ],
        },
      ],
    },
    mostRequested: {
      eyebrow: "— Nejžádanější školení —",
      headlineLines: ["WORKSHOPY", "ŘÍZENÍ"],
      headlineAccent: "ZMĚN",
      paragraph:
        "Protože první čtvrtina 21. století zůstává turbulentní, můžeme čekat ještě více změn. Firmy, týmy i jednotlivci se musí rychle přizpůsobovat novým trendům a technologickému pokroku. Praktické nástroje, postupy a reálné příklady řízení změn pomáhají organizacím zvládat rychlé posuny a nejistotu.",
      cta: "Získat více informací",
    },
    references: {
      eyebrow: "— Důvěřují mi —",
      titleMain: "KLIENTSKÉ",
      titleAccent: "REFERENCE",
    },
    reviews: {
      eyebrow: "— Klientské recenze —",
      titleMain: "Recenze &",
      titleAccent: "Doporučení",
    },
    cta: {
      headline1: "POJĎME",
      headline2: "ZAČÍT.",
      headline3a: "JDEME",
      headline3b: "NA TO?",
      paragraph:
        "Vyberte si 30minutový úvodní hovor, zavolejte přímo, nebo pošlete zprávu — vrátím se vám s konkrétním dalším krokem do jednoho pracovního dne.",
      phone: "+420 602 461 799",
      schedule: "Naplánovat hovor",
      leave: "Poslat zprávu",
    },
    footer: {
      tagline:
        "Konzultace, mentoring a praktická školení pro IT manažery a týmy. Žádné zázračné recepty — jen řešení, která fungují ve vaší realitě.",
      navigate: "Navigace",
      home: "Domů",
      services: "Služby",
      references: "Reference",
      reviews: "Recenze",
      getInTouch: "Spojte se",
      rights: "Všechna práva vyhrazena.",
      crafted: "Vytvořeno pro změnu s dopadem",
    },
  },
};

export default TRANSLATIONS;
